import { TFunction } from 'i18next';
import { shuffle } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ToggleChipOption } from 'src/routes/onboarding/setup/components/ToggleChipGroup';

export interface CustomizationOptions {
  priorityOptions: ToggleChipOption[];
  featureOptions: ToggleChipOption[];
}

export const getPriorityLabels = (t: TFunction) => ({
  acquire_clients: t(($) => $.page_onboarding.goals.acquire_clients),
  retain_clients: t(($) => $.page_onboarding.goals.retain_clients),
  improve_client_onboarding: t(
    ($) => $.page_onboarding.goals.improve_onboarding,
  ),
  enhance_client_experience: t(
    ($) => $.page_onboarding.goals.enhance_experience,
  ),
  improve_collaboration: 'Improve internal collaboration',
  reduce_costs: 'Reduce operational costs',
  automate_workflows: 'Automate workflows',
  centralize_information: t(($) => $.page_onboarding.goals.centralize_info),
});

export const useRandomizedCustomizationOptions = (): CustomizationOptions => {
  const { t } = useTranslation();

  const PRIORITY_OPTIONS = useMemo(() => {
    const priorityLabels = getPriorityLabels(t);
    return Object.keys(priorityLabels).reduce(
      (acc, curr) => [
        ...acc,
        {
          label: priorityLabels[curr as keyof typeof priorityLabels],
          value: curr,
        },
      ],
      [] as ToggleChipOption[],
    );
  }, [t]);

  const FEATURE_OPTIONS: ToggleChipOption[] = [
    { label: t(($) => $.page_plans.client_portal), value: 'client_home' },
    { label: 'Payments', value: 'payments' },
    { label: 'Forms', value: 'forms' },
    { label: 'File-sharing', value: 'files' },
    { label: 'Contracts', value: 'contracts' },
    { label: 'Tasks', value: 'tasks' },
    { label: 'Scheduling', value: 'scheduling' },
    { label: 'Embeds', value: 'embeds' },
    { label: 'Developer Platform', value: 'developer_platform' },
  ];

  const priorityOptions = useMemo(
    () => shuffle(PRIORITY_OPTIONS),
    [shuffle, PRIORITY_OPTIONS],
  );

  const featureOptions = useMemo(
    () => shuffle(FEATURE_OPTIONS),
    [shuffle, FEATURE_OPTIONS],
  );

  return { priorityOptions, featureOptions };
};
